import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import {
  CogIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  LightBulbIcon,
  KeyIcon
} from '@heroicons/react/24/outline';

export default function CustomMetadataPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to={`${basePath}/help`} className="text-gray-500 hover:text-gray-700">
                Help Center
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-gray-500 hover:text-gray-700">
                Best Practices
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900">Custom Metadata Types</li>
          </ol>
        </nav>

        {/* Back Button */}
        <Link 
          to={`${basePath}/help/best-practices`} 
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Best Practices
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Using Custom Metadata Types in Flows
          </h1>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-red-100 text-red-800">
              Advanced
            </span>
            <span>10 min read</span>
            <span>Configuration</span>
          </div>
        </div>

        {/* Key Concept */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <KeyIcon className="h-6 w-6 text-blue-600 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Why Custom Metadata?</h3>
              <p className="text-blue-800">
                Store configuration values that can be deployed, changed without code, and accessed efficiently in flows.
              </p>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Benefits Over Hardcoded Values
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                Deployable
              </h3>
              <p className="text-gray-700">
                Custom Metadata records can be deployed through change sets, packages, and metadata API.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                No Governor Limit Impact
              </h3>
              <p className="text-gray-700">
                Queries to Custom Metadata don't count against SOQL limits in flows.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                Admin-Friendly
              </h3>
              <p className="text-gray-700">
                Non-developers can update configuration values through the UI.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                Environment-Specific
              </h3>
              <p className="text-gray-700">
                Different values can be maintained for sandbox vs production.
              </p>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Perfect Use Cases</h2>
          
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Integration Endpoints</h4>
              <p className="text-gray-700">Store API URLs, credentials, and configuration for external systems.</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Business Rules</h4>
              <p className="text-gray-700">Discount percentages, approval thresholds, SLA timings.</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Feature Flags</h4>
              <p className="text-gray-700">Enable/disable flow functionality without deploying changes.</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Email Templates & Messages</h4>
              <p className="text-gray-700">Store template IDs, standard messages, and communication settings.</p>
            </div>
          </div>
        </section>

        {/* Implementation Example */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Implementation Example</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Step 1: Create Custom Metadata Type</h3>
              <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto">
{`Label: Flow Configuration
Plural: Flow Configurations
Object Name: Flow_Configuration__mdt

Fields:
- Key__c (Text, Unique)
- Value__c (Text)
- Description__c (Text)`}
              </pre>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Step 2: Query in Flow</h3>
              <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto">
{`Get Records: Flow_Configuration__mdt
Filter: Key__c = "Approval_Threshold"
Store: {!Config_Record}`}
              </pre>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Step 3: Use the Value</h3>
              <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto">
{`Decision: Check Approval Needed
If: {!Opportunity.Amount} > {!Config_Record.Value__c}
Then: Route to Approval Process`}
              </pre>
            </div>
          </div>
        </section>

        {/* Best Practices */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Best Practices</h2>
          
          <ul className="space-y-3">
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Use descriptive keys that won't change (like DeveloperNames)</span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Include descriptions to document what each configuration controls</span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Group related configurations using prefixes (e.g., "Integration_", "Approval_")</span>
            </li>
            <li className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
              <span>Consider using hierarchical Custom Metadata for complex scenarios</span>
            </li>
          </ul>
        </section>

        {/* Call to Action */}
        <div className="bg-gray-100 rounded-lg p-6 text-center">
          <p className="text-gray-700 mb-4">
            Ready to make your flows more configurable and maintainable?
          </p>
          <Link 
            to={`${basePath}/flows`} 
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Review Your Flows
          </Link>
        </div>
      </div>
    </div>
  );
}